/**
 * Mobile Drawer Navigation
 * Opens/closes the side menu on mobile. Self-contained focus management —
 * traps Tab within the open drawer and restores focus to the trigger on close
 * (WCAG 2.1.2 / 2.4.3). No external dependencies, so it works on every page.
 */
class MobileDrawer {
    constructor() {
        this.drawer = document.getElementById('MobileDrawer');
        this.triggers = document.querySelectorAll('[data-mobile-menu-toggle]');
        this.closeButtons = document.querySelectorAll('[data-mobile-menu-close]');

        if (!this.drawer) return;

        this.desktopMedia = window.matchMedia('(min-width: 1051px)');
        this.lastFocused = null;
        this.onKeydown = this.onKeydown.bind(this);
        this.onDesktopChange = this.onDesktopChange.bind(this);

        this.bindEvents();
        this.triggers.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
    }

    bindEvents() {
        this.triggers.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.open(e.currentTarget);
            });
        });

        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        if (typeof this.desktopMedia.addEventListener === 'function') {
            this.desktopMedia.addEventListener('change', this.onDesktopChange);
        } else {
            this.desktopMedia.addListener(this.onDesktopChange);
        }
    }

    isOpen() {
        return this.drawer.classList.contains('is-open');
    }

    isVisible(el) {
        if (!el || !el.isConnected) return false;

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
            return false;
        }

        return el.offsetParent !== null || el.getClientRects().length > 0;
    }

    getFocusable() {
        const sel = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.prototype.filter.call(
            this.drawer.querySelectorAll(sel),
            el => el.tabIndex >= 0 && this.isVisible(el)
        );
    }

    onDesktopChange(e) {
        if (e.matches && this.isOpen()) this.close(false);
    }

    onKeydown(e) {
        if (!this.isOpen()) return;

        if (e.key === 'Escape') {
            this.close();
            return;
        }

        if (e.key !== 'Tab') return;

        const focusable = this.getFocusable();
        if (focusable.length === 0) {
            e.preventDefault();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
            if (active === first || !this.drawer.contains(active)) {
                e.preventDefault();
                last.focus();
            }
        } else if (active === last || !this.drawer.contains(active)) {
            e.preventDefault();
            first.focus();
        }
    }

    open(trigger) {
        if (this.isOpen()) return;
        this.lastFocused = trigger;

        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.triggers.forEach(btn => btn.setAttribute('aria-expanded', 'true'));

        document.addEventListener('keydown', this.onKeydown);

        const focusable = this.getFocusable();
        if (focusable.length) focusable[0].focus();
    }

    close(restoreFocus = true) {
        if (!this.isOpen()) return;

        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        this.triggers.forEach(btn => btn.setAttribute('aria-expanded', 'false'));

        document.removeEventListener('keydown', this.onKeydown);

        if (restoreFocus) {
            let restore = this.isVisible(this.lastFocused) ? this.lastFocused : null;

            if (!restore) {
                const currentTriggers = document.querySelectorAll('[data-mobile-menu-toggle]');
                for (let i = 0; i < currentTriggers.length; i++) {
                    if (this.isVisible(currentTriggers[i])) {
                        restore = currentTriggers[i];
                        break;
                    }
                }
            }

            if (restore && typeof restore.focus === 'function') restore.focus();
        }
        this.lastFocused = null;
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.mobileDrawer = new MobileDrawer();
});
